import {
    Cancel,
    CheckCircle,
    Close,
    Edit,
    Email,
    ExpandLess,
    ExpandMore,
    Info,
    Person as PersonIcon,
    Phone,
    School as SchoolIcon,
    Psychology as SuperPowerIcon,
    Work as WorkIcon,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    ButtonGroup,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';

import { useOnboardingStrings } from '../data/OnboardingLangStrings';
import { getSkillLevelInfo, getTextColor, SKILL_LEVELS } from '../data/skillLevels';

interface MobileCVAnalysisResultsStepProps {
    onNext: () => void;
    onBack: () => void;
    setAgentMessage: (message: string) => void;
    currentSubStep: number;
    onDataUpdate?: (data: any) => void;
}

export default function MobileCVAnalysisResultsStep({
    onNext,
    onBack,
    setAgentMessage,
    currentSubStep,
    onDataUpdate,
}: MobileCVAnalysisResultsStepProps) {
    const theme = useTheme();
    const strings = useOnboardingStrings('de');

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedSection, setSelectedSection] = useState<any>(null);
    const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
    const [tempLevel, setTempLevel] = useState<1 | 2 | 3 | 4>(1);

    const [personalInfo, setPersonalInfo] = useState({
        firstName: 'Max',
        lastName: 'Mustermann',
        email: 'max.mustermann@firma.de',
        phone: '+49 123 456789',
        shortDescription: 'Erfahrener Frontend-Entwickler mit Fokus auf React und TypeScript.',
    });

    const [skills, setSkills] = useState([
        // Soft Skills
        { id: 1, name: 'Kommunikation', status: 'pending', level: 4, category: 'Soft Skills' },
        { id: 2, name: 'Teamarbeit', status: 'pending', level: 3, category: 'Soft Skills' },
        { id: 3, name: 'Problemlösung', status: 'pending', level: 4, category: 'Soft Skills' },
        { id: 4, name: 'Führungsqualitäten', status: 'pending', level: 2, category: 'Soft Skills' },
        // Hard Skills
        { id: 5, name: 'React.js', status: 'pending', level: 4, category: 'Hard Skills' },
        { id: 6, name: 'TypeScript', status: 'pending', level: 3, category: 'Hard Skills' },
        { id: 7, name: 'Node.js', status: 'pending', level: 3, category: 'Hard Skills' },
        { id: 8, name: 'Python', status: 'pending', level: 2, category: 'Hard Skills' },
        // Industry Skills
        { id: 9, name: 'Agile Methoden', status: 'pending', level: 3, category: 'Industry Skills' },
        { id: 10, name: 'DevOps', status: 'pending', level: 2, category: 'Industry Skills' },
        { id: 11, name: 'Cloud Computing', status: 'pending', level: 3, category: 'Industry Skills' },
        { id: 12, name: 'Projektmanagement', status: 'pending', level: 2, category: 'Industry Skills' },
    ]);

    const [superPowers, setSuperPowers] = useState([
        { id: 1, name: 'React.js', status: 'pending', level: 3, category: 'Hard Skills' },
        { id: 2, name: 'TypeScript', status: 'pending', level: 3, category: 'Hard Skills' },
        { id: 3, name: 'Kommunikation', status: 'pending', level: 3, category: 'Soft Skills' },
    ]);

    const [certificates, setCertificates] = useState([
        { id: 1, name: 'AWS Certified Developer', status: 'pending' },
        { id: 2, name: 'React Professional Certificate', status: 'pending' },
        { id: 3, name: 'Scrum Master Certification', status: 'pending' },
    ]);

    const [roles, setRoles] = useState([
        {
            id: 1,
            name: 'Senior Frontend Developer',
            status: 'pending',
            description: 'Lead frontend development projects',
        },
        {
            id: 2,
            name: 'Full-Stack Developer',
            status: 'pending',
            description: 'Work across frontend and backend technologies',
        },
        {
            id: 3,
            name: 'Technical Lead',
            status: 'pending',
            description: 'Guide technical decisions and mentor team members',
        },
    ]);

    // Check if all sections are complete
    const isPersonalInfoComplete = personalInfo.firstName && personalInfo.lastName && personalInfo.email;
    const isSkillsComplete = skills.every((skill) => skill.status !== 'pending');
    const isSuperPowersComplete = superPowers.every((sp) => sp.status !== 'pending');
    const isCertificatesComplete = certificates.every((cert) => cert.status !== 'pending');
    const isRolesComplete = roles.every((role) => role.status !== 'pending');

    const isAllComplete =
        isPersonalInfoComplete &&
        isSkillsComplete &&
        isSuperPowersComplete &&
        isCertificatesComplete &&
        isRolesComplete;

    const sections = [
        {
            id: 'personal',
            title: 'Persönliche Daten',
            icon: <PersonIcon />,
            data: [personalInfo],
            color: theme.palette.primary.main,
            count: 1,
        },
        {
            id: 'skills',
            title: 'Skills',
            icon: <PersonIcon />,
            data: skills,
            color: theme.palette.skill?.main || theme.palette.primary.main,
            count: skills.length,
        },
        {
            id: 'superPowers',
            title: 'SuperPowers',
            icon: <SuperPowerIcon />,
            data: superPowers,
            color: theme.palette.secondary.main,
            count: superPowers.length,
        },
        {
            id: 'certificates',
            title: 'Zertifikate',
            icon: <SchoolIcon />,
            data: certificates,
            color: theme.palette.certificate?.main || theme.palette.warning.main,
            count: certificates.length,
        },
        {
            id: 'roles',
            title: 'Rollen',
            icon: <WorkIcon />,
            data: roles,
            color: theme.palette.info.main,
            count: roles.length,
        },
    ];

    const subsectionMessages = [
        'Überprüfen Sie Ihre persönlichen Daten und passen Sie diese bei Bedarf an.',
        'Bewerten Sie die erkannten Skills und passen Sie die Level entsprechend an.',
        'Wählen Sie bis zu 3 Ihrer stärksten Fähigkeiten als SuperPowers aus.',
        'Überprüfen Sie die gefundenen Zertifikate und bestätigen Sie diese.',
        'Wählen Sie passende Rollen basierend auf Ihren Fähigkeiten aus.',
    ];

    useEffect(() => {
        setAgentMessage('Überprüfen und bestätigen Sie Ihre CV-Analyse Ergebnisse.');
    }, [setAgentMessage]);

    // Update parent component with data changes
    useEffect(() => {
        if (onDataUpdate) {
            onDataUpdate({
                personalInfo,
                skills,
                superPowers,
                certificates,
                roles,
            });
        }
    }, [personalInfo, skills, superPowers, certificates, roles, onDataUpdate]);

    const handleItemClick = (section: any, item?: any) => {
        setSelectedSection({ ...section, selectedItem: item });
        if (item?.level) {
            setTempLevel(item.level);
        }
        setDialogOpen(true);
    };

    const toggleSection = (sectionId: string) => {
        setExpandedSections((prev) => ({
            ...prev,
            [sectionId]: !prev[sectionId],
        }));
    };

    const renderPersonalInfoDialog = () => (
        <DialogContent sx={{ p: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    fullWidth
                    label="Vorname"
                    value={personalInfo.firstName}
                    onChange={(e) => setPersonalInfo((prev) => ({ ...prev, firstName: e.target.value }))}
                    size="small"
                />
                <TextField
                    fullWidth
                    label="Nachname"
                    value={personalInfo.lastName}
                    onChange={(e) => setPersonalInfo((prev) => ({ ...prev, lastName: e.target.value }))}
                    size="small"
                />
                <TextField
                    fullWidth
                    label="E-Mail"
                    value={personalInfo.email}
                    onChange={(e) => setPersonalInfo((prev) => ({ ...prev, email: e.target.value }))}
                    size="small"
                    type="email"
                />
                <TextField
                    fullWidth
                    label="Telefon"
                    value={personalInfo.phone}
                    onChange={(e) => setPersonalInfo((prev) => ({ ...prev, phone: e.target.value }))}
                    size="small"
                    type="tel"
                />
                <TextField
                    fullWidth
                    label="Kurzbeschreibung"
                    value={personalInfo.shortDescription}
                    onChange={(e) => setPersonalInfo((prev) => ({ ...prev, shortDescription: e.target.value }))}
                    size="small"
                    multiline
                    rows={3}
                />
            </Box>
        </DialogContent>
    );

    const renderSkillOrCompetenceDialog = (item: any) => {
        const levelInfo = getSkillLevelInfo(tempLevel);

        const handleLevelChange = (newLevel: 1 | 2 | 3 | 4) => {
            setTempLevel(newLevel);
        };

        const handleAccept = () => {
            if (selectedSection?.id === 'skills') {
                setSkills((prev) =>
                    prev.map((skill) =>
                        skill.id === item.id ? { ...skill, level: tempLevel, status: 'accepted' } : skill,
                    ),
                );
            } else if (selectedSection?.id === 'superPowers') {
                setSuperPowers((prev) =>
                    prev.map((sp) => (sp.id === item.id ? { ...sp, level: tempLevel, status: 'accepted' } : sp)),
                );
            }
            setDialogOpen(false);
        };

        const handleReject = () => {
            if (selectedSection?.id === 'skills') {
                setSkills((prev) =>
                    prev.map((skill) => (skill.id === item.id ? { ...skill, status: 'rejected' } : skill)),
                );
            } else if (selectedSection?.id === 'superPowers') {
                setSuperPowers((prev) => prev.map((sp) => (sp.id === item.id ? { ...sp, status: 'rejected' } : sp)));
            }
            setDialogOpen(false);
        };

        return (
            <DialogContent sx={{ p: '16px' }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
                        {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {item.category}
                    </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                        Aktuelles Level:
                    </Typography>
                    <Chip
                        label={levelInfo.name}
                        sx={{
                            backgroundColor: levelInfo.color,
                            color: getTextColor(tempLevel),
                            fontWeight: 500,
                            mb: 2,
                        }}
                    />

                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Level anpassen:
                    </Typography>
                    <ButtonGroup size="small" fullWidth>
                        {SKILL_LEVELS.map((level) => (
                            <Button
                                key={level.level}
                                variant={tempLevel === level.level ? 'contained' : 'outlined'}
                                onClick={() => handleLevelChange(level.level as 1 | 2 | 3 | 4)}
                                sx={{
                                    backgroundColor: tempLevel === level.level ? level.color : undefined,
                                    color: tempLevel === level.level ? getTextColor(level.level) : 'text.primary',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {level.level}
                            </Button>
                        ))}
                    </ButtonGroup>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircle />}
                        sx={{ flex: 1 }}
                        onClick={handleAccept}
                    >
                        Annehmen
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<Cancel />}
                        sx={{ flex: 1 }}
                        onClick={handleReject}
                    >
                        Ablehnen
                    </Button>
                </Box>
            </DialogContent>
        );
    };

    const renderGenericDialog = (item: any) => {
        const handleAccept = () => {
            if (selectedSection?.id === 'certificates') {
                setCertificates((prev) =>
                    prev.map((cert) => (cert.id === item.id ? { ...cert, status: 'accepted' } : cert)),
                );
            } else if (selectedSection?.id === 'roles') {
                setRoles((prev) => prev.map((role) => (role.id === item.id ? { ...role, status: 'accepted' } : role)));
            }
            setDialogOpen(false);
        };

        const handleReject = () => {
            if (selectedSection?.id === 'certificates') {
                setCertificates((prev) =>
                    prev.map((cert) => (cert.id === item.id ? { ...cert, status: 'rejected' } : cert)),
                );
            } else if (selectedSection?.id === 'roles') {
                setRoles((prev) => prev.map((role) => (role.id === item.id ? { ...role, status: 'rejected' } : role)));
            }
            setDialogOpen(false);
        };

        return (
            <DialogContent sx={{ p: '16px' }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
                        {item.name}
                    </Typography>
                    {item.description && (
                        <Typography variant="body2" color="text.secondary">
                            {item.description}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircle />}
                        sx={{ flex: 1 }}
                        onClick={handleAccept}
                    >
                        Annehmen
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<Cancel />}
                        sx={{ flex: 1 }}
                        onClick={handleReject}
                    >
                        Ablehnen
                    </Button>
                </Box>
            </DialogContent>
        );
    };

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Compact Header */}
            <Box sx={{ p: '16px', pb: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        CV Analyse
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {currentSubStep + 1} / {sections.length}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Button size="small" onClick={onBack} disabled={currentSubStep === 0} sx={{ fontSize: '0.75rem' }}>
                        Zurück
                    </Button>
                    <Button
                        size="small"
                        onClick={onNext}
                        variant="contained"
                        disabled={!isAllComplete}
                        sx={{
                            fontSize: '0.75rem',
                            ...(isAllComplete && {
                                background: `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.primary.main} 90%)`,
                                boxShadow: `0 0 20px ${alpha(theme.palette.success.main, 0.5)}`,
                                animation: 'glow 2s ease-in-out infinite alternate',
                                '@keyframes glow': {
                                    from: {
                                        boxShadow: `0 0 20px ${alpha(theme.palette.success.main, 0.5)}`,
                                    },
                                    to: {
                                        boxShadow: `0 0 30px ${alpha(theme.palette.success.main, 0.8)}`,
                                    },
                                },
                                '&:hover': {
                                    background: `linear-gradient(45deg, ${theme.palette.success.dark} 30%, ${theme.palette.primary.dark} 90%)`,
                                    boxShadow: `0 0 25px ${alpha(theme.palette.success.main, 0.7)}`,
                                },
                            }),
                        }}
                    >
                        {currentSubStep === sections.length - 1 ? 'Fertig' : 'Weiter'}
                    </Button>
                </Box>
            </Box>

            {/* Main Content */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {/* Personal Info Section */}
                <Box
                    sx={{
                        mb: 2,
                        p: '16px',
                        border: `1px solid ${alpha(isPersonalInfoComplete ? theme.palette.success.main : theme.palette.primary.main, 0.3)}`,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(isPersonalInfoComplete ? theme.palette.success.main : theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                            sx={{
                                bgcolor: isPersonalInfoComplete
                                    ? theme.palette.success.main
                                    : theme.palette.primary.main,
                                width: 28,
                                height: 28,
                            }}
                        >
                            {isPersonalInfoComplete ? <CheckCircle /> : <PersonIcon />}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                Persönliche Daten
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                1 Element
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => toggleSection('personal')}>
                            {expandedSections['personal'] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>

                    <Collapse in={expandedSections['personal']}>
                        <List sx={{ p: 0 }}>
                            <ListItem
                                sx={{ px: 0, py: 1, cursor: 'pointer' }}
                                onClick={() => handleItemClick(sections[0])}
                            >
                                <ListItemText
                                    primary={`${personalInfo.firstName} ${personalInfo.lastName}`}
                                    secondary={
                                        <Box sx={{ mt: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Info sx={{ fontSize: 12, color: 'text.secondary' }} />
                                                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                                    {personalInfo.shortDescription}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Email sx={{ fontSize: 12, color: 'text.secondary' }} />
                                                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                                    {personalInfo.email}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Phone sx={{ fontSize: 12, color: 'text.secondary' }} />
                                                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                                    {personalInfo.phone}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    }
                                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                                />
                                <Edit sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </ListItem>
                        </List>
                    </Collapse>
                </Box>

                {/* Skills Section */}
                <Box
                    sx={{
                        mb: 2,
                        p: '8px',
                        border: `1px solid ${alpha(isSkillsComplete ? theme.palette.success.main : theme.palette.skill?.main || theme.palette.primary.main, 0.3)}`,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(isSkillsComplete ? theme.palette.success.main : theme.palette.skill?.main || theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                            sx={{
                                bgcolor: isSkillsComplete
                                    ? theme.palette.success.main
                                    : theme.palette.skill?.main || theme.palette.primary.main,
                                width: 28,
                                height: 28,
                            }}
                        >
                            {isSkillsComplete ? <CheckCircle /> : <PersonIcon />}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                Skills
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {skills.length} Elemente
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => toggleSection('skills')}>
                            {expandedSections['skills'] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>

                    <Collapse in={expandedSections['skills']}>
                        <List sx={{ p: 0 }}>
                            {Object.entries(
                                skills.reduce(
                                    (acc, skill) => {
                                        if (!acc[skill.category]) acc[skill.category] = [];
                                        acc[skill.category].push(skill);
                                        return acc;
                                    },
                                    {} as Record<string, typeof skills>,
                                ),
                            )
                                .sort(([categoryA], [categoryB]) => {
                                    const order = ['Soft Skills', 'Hard Skills', 'Industry Skills'];
                                    const indexA = order.indexOf(categoryA);
                                    const indexB = order.indexOf(categoryB);
                                    if (indexA === -1 && indexB === -1) return categoryA.localeCompare(categoryB);
                                    if (indexA === -1) return 1;
                                    if (indexB === -1) return -1;
                                    return indexA - indexB;
                                })
                                .map(([category, categorySkills]) => (
                                    <Box key={category}>
                                        <Box
                                            sx={{
                                                px: 1,
                                                py: 1,
                                                mb: 1,
                                                mt: category !== 'Soft Skills' ? 2 : 0,
                                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                borderRadius: 1,
                                                borderLeft: `3px solid ${theme.palette.primary.main}`,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 0.5,
                                                }}
                                            >
                                                {category}
                                            </Typography>
                                        </Box>
                                        {categorySkills.map((item, index) => (
                                            <ListItem
                                                key={item.id}
                                                sx={{
                                                    px: 1,
                                                    py: 1,
                                                    cursor: 'pointer',
                                                    bgcolor:
                                                        index % 2 === 0
                                                            ? 'transparent'
                                                            : alpha(theme.palette.primary.main, 0.03),
                                                    borderRadius: 1,
                                                    ml: 1,
                                                }}
                                                onClick={() => handleItemClick(sections[1], item)}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={{ fontSize: '0.85rem' }}>{item.name}</Typography>
                                                    <Chip
                                                        label={getSkillLevelInfo(item.level as 1 | 2 | 3 | 4).name}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: getSkillLevelInfo(
                                                                item.level as 1 | 2 | 3 | 4,
                                                            ).color,
                                                            color: getTextColor(item.level as 1 | 2 | 3 | 4),
                                                            fontSize: '0.7rem',
                                                            height: 18,
                                                            mt: 0.5,
                                                        }}
                                                    />
                                                </Box>
                                                <Chip
                                                    size="small"
                                                    label={
                                                        item.status === 'pending'
                                                            ? 'Prüfen'
                                                            : item.status === 'accepted'
                                                              ? '✓'
                                                              : '✗'
                                                    }
                                                    color={
                                                        item.status === 'accepted'
                                                            ? 'success'
                                                            : item.status === 'rejected'
                                                              ? 'error'
                                                              : 'default'
                                                    }
                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                />
                                            </ListItem>
                                        ))}
                                    </Box>
                                ))}
                        </List>
                    </Collapse>
                </Box>

                {/* SuperPowers Section */}
                <Box
                    sx={{
                        mb: 2,
                        p: '16px',
                        border: `1px solid ${alpha(isSuperPowersComplete ? theme.palette.success.main : theme.palette.secondary.main, 0.3)}`,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(isSuperPowersComplete ? theme.palette.success.main : theme.palette.secondary.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                            sx={{
                                bgcolor: isSuperPowersComplete
                                    ? theme.palette.success.main
                                    : theme.palette.secondary.main,
                                width: 28,
                                height: 28,
                            }}
                        >
                            {isSuperPowersComplete ? <CheckCircle /> : <SuperPowerIcon />}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                SuperPowers
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {superPowers.length} Elemente (max. 3)
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => toggleSection('superPowers')}>
                            {expandedSections['superPowers'] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>

                    <Collapse in={expandedSections['superPowers']}>
                        <List sx={{ p: 0 }}>
                            {superPowers.map((item, index) => (
                                <ListItem
                                    key={item.id}
                                    sx={{
                                        px: 0,
                                        py: 1,
                                        cursor: 'pointer',
                                        bgcolor:
                                            index % 2 === 0 ? 'transparent' : alpha(theme.palette.secondary.main, 0.03),
                                        borderRadius: 1,
                                    }}
                                    onClick={() => handleItemClick(sections[2], item)}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: '0.85rem' }}>{item.name}</Typography>
                                        <Chip
                                            label={getSkillLevelInfo(item.level as 1 | 2 | 3 | 4).name}
                                            size="small"
                                            sx={{
                                                backgroundColor: getSkillLevelInfo(item.level as 1 | 2 | 3 | 4).color,
                                                color: 'var(--color-skill-level-text)',
                                                fontSize: '0.7rem',
                                                height: 18,
                                                mt: 0.5,
                                            }}
                                        />
                                    </Box>
                                    <Chip
                                        size="small"
                                        label={
                                            item.status === 'pending'
                                                ? 'Prüfen'
                                                : item.status === 'accepted'
                                                  ? '✓'
                                                  : '✗'
                                        }
                                        color={
                                            item.status === 'accepted'
                                                ? 'success'
                                                : item.status === 'rejected'
                                                  ? 'error'
                                                  : 'default'
                                        }
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                </Box>

                {/* Certificates Section */}
                <Box
                    sx={{
                        mb: 2,
                        p: '16px',
                        border: `1px solid ${alpha(isCertificatesComplete ? theme.palette.success.main : theme.palette.certificate?.main || theme.palette.warning.main, 0.3)}`,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(isCertificatesComplete ? theme.palette.success.main : theme.palette.certificate?.main || theme.palette.warning.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                            sx={{
                                bgcolor: isCertificatesComplete
                                    ? theme.palette.success.main
                                    : theme.palette.certificate?.main || theme.palette.warning.main,
                                width: 28,
                                height: 28,
                            }}
                        >
                            {isCertificatesComplete ? <CheckCircle /> : <SchoolIcon />}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                Zertifikate
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {certificates.length} Elemente
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => toggleSection('certificates')}>
                            {expandedSections['certificates'] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>

                    <Collapse in={expandedSections['certificates']}>
                        <List sx={{ p: 0 }}>
                            {certificates.map((item, index) => (
                                <ListItem
                                    key={item.id}
                                    sx={{
                                        px: 0,
                                        py: 1,
                                        cursor: 'pointer',
                                        bgcolor:
                                            index % 2 === 0 ? 'transparent' : alpha(theme.palette.warning.main, 0.03),
                                        borderRadius: 1,
                                    }}
                                    onClick={() => handleItemClick(sections[3], item)}
                                >
                                    <ListItemText
                                        primary={item.name}
                                        primaryTypographyProps={{ fontSize: '0.85rem' }}
                                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                    />
                                    <Chip
                                        size="small"
                                        label={
                                            item.status === 'pending'
                                                ? 'Prüfen'
                                                : item.status === 'accepted'
                                                  ? '✓'
                                                  : '✗'
                                        }
                                        color={
                                            item.status === 'accepted'
                                                ? 'success'
                                                : item.status === 'rejected'
                                                  ? 'error'
                                                  : 'default'
                                        }
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                </Box>

                {/* Roles Section */}
                <Box
                    sx={{
                        mb: 2,
                        p: '16px',
                        border: `1px solid ${alpha(isRolesComplete ? theme.palette.success.main : theme.palette.info.main, 0.3)}`,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(isRolesComplete ? theme.palette.success.main : theme.palette.info.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                            sx={{
                                bgcolor: isRolesComplete ? theme.palette.success.main : theme.palette.info.main,
                                width: 28,
                                height: 28,
                            }}
                        >
                            {isRolesComplete ? <CheckCircle /> : <WorkIcon />}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                Rollen
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {roles.length} Elemente
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => toggleSection('roles')}>
                            {expandedSections['roles'] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>

                    <Collapse in={expandedSections['roles']}>
                        <List sx={{ p: 0 }}>
                            {roles.map((item, index) => (
                                <ListItem
                                    key={item.id}
                                    sx={{
                                        px: 0,
                                        py: 1,
                                        cursor: 'pointer',
                                        bgcolor: index % 2 === 0 ? 'transparent' : alpha(theme.palette.info.main, 0.03),
                                        borderRadius: 1,
                                    }}
                                    onClick={() => handleItemClick(sections[4], item)}
                                >
                                    <ListItemText
                                        primary={item.name}
                                        secondary={item.description}
                                        primaryTypographyProps={{ fontSize: '0.85rem' }}
                                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                    />
                                    <Chip
                                        size="small"
                                        label={
                                            item.status === 'pending'
                                                ? 'Prüfen'
                                                : item.status === 'accepted'
                                                  ? '✓'
                                                  : '✗'
                                        }
                                        color={
                                            item.status === 'accepted'
                                                ? 'success'
                                                : item.status === 'rejected'
                                                  ? 'error'
                                                  : 'default'
                                        }
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                </Box>
            </Box>

            {/* Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        m: 2,
                        maxHeight: '80vh',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                        borderRadius: 3,
                        background: theme.palette.background.paper,
                        backdropFilter: 'blur(15px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '1px',
                            background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.6)}, transparent)`,
                        },
                    },
                }}
            >
                <DialogTitle sx={{ p: '16px', pb: '8px', '&.MuiDialogTitle-root': { p: '16px', pb: '8px' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                            {selectedSection?.title}
                        </Typography>
                        <IconButton size="small" onClick={() => setDialogOpen(false)}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>

                {selectedSection?.id === 'personal' && renderPersonalInfoDialog()}
                {(selectedSection?.id === 'skills' || selectedSection?.id === 'superPowers') &&
                    selectedSection.selectedItem &&
                    renderSkillOrCompetenceDialog(selectedSection.selectedItem)}
                {selectedSection?.id !== 'personal' &&
                    selectedSection?.id !== 'skills' &&
                    selectedSection?.id !== 'superPowers' &&
                    selectedSection?.selectedItem &&
                    renderGenericDialog(selectedSection.selectedItem)}

                <DialogActions sx={{ p: '16px', pt: '8px' }}>
                    <Button onClick={() => setDialogOpen(false)} size="small">
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
